import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import { validateRequired } from '../../../../../util';
import Regulation, { DEFAULT_LEVELS_3X3, DEFAULT_LEVELS_5X4 } from './Regulation';
import { useSave1, useSearchDep } from './Regulation_QUERY';
import {
  Regulation_Schema,
  MapRisCustomGridRowToRegulationForm,
} from './Regulation_Schema';

const getDefaultForm = () => ({
  rsCd: null,
  rsNm: '',
  rsGubun: '최초평가',
  rsMethod: '빈도·강도법',
  depNm: [],
  depNmCsv: null,
  startDate: dayjs().format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  scale: '3x3',
  levels: DEFAULT_LEVELS_3X3,
});

/**
 * Step00_Regulation 기능단
 * - 화면단(Regulation)에 상태/저장 로직을 주입
 */
function RegulationF({ onCancel, onSaved, initialMasterRow = null }) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const searchInfo = useSelector((s) => s.SearchInfo);

  const [deptItems, setDeptItems] = useState([]);

  const { run: SearchDep } = useSearchDep();
  useEffect(() => {
    SearchDep(undefined, {
      onSuccess: (rows) => {
        const list = (rows || []).map((item) => ({
          value: item.depCd,
          label: item.depNmLast ?? item.depNm ?? item.depCd,
        }));
        setDeptItems(list);
      },
    });
    // 마운트 시 1회만 부서 조회 (SearchDep 의존 시 매 렌더마다 새 참조로 무한 호출됨)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState(() => getDefaultForm());

  /** 신규(씨드 없음): 기본 폼만. 수정: GetRisCustomR1 그리드 행 → 폼 바인딩 */
  useEffect(() => {
    if (initialMasterRow) return;
    setForm(getDefaultForm());
  }, [initialMasterRow]);

  useEffect(() => {
    if (!initialMasterRow) return;
    const partial = MapRisCustomGridRowToRegulationForm(
      initialMasterRow,
      deptItems
    );
    if (!partial) return;
    const scale = partial.scale ?? '3x3';
    const levels =
      Array.isArray(partial.levels) && partial.levels.length > 0
        ? partial.levels
        : scale === '5x4'
          ? DEFAULT_LEVELS_5X4
          : DEFAULT_LEVELS_3X3;
    setForm({
      ...getDefaultForm(),
      ...partial,
      scale,
      levels,
    });
  }, [initialMasterRow, deptItems]);

  const { runAsync: Save1Async } = useSave1({
    onError: (e) => {
      dispatch(
        showSnackbar({
          message: e?.message || '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    },
  });

  const handleChange = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...(patch || {}) };
      // 평가척도 변경 시 기본 위험성 수준 템플릿 재적용
      if (patch?.scale && patch.scale !== prev.scale) {
        next.levels = patch.scale === '5x4' ? DEFAULT_LEVELS_5X4 : DEFAULT_LEVELS_3X3;
      }
      return next;
    });
  };

  const SaveLogic1 = async () => {
    const depList = Array.isArray(form.depNm)
      ? form.depNm
      : typeof form.depNm === 'string'
        ? form.depNm.split(',').filter(Boolean)
        : [];
    const depCdCsv = depList.length ? depList.join(',') : null;

    const row = {
      ...form,
      depNm: depList,
      depCd: depCdCsv,
      depNmCsv: depCdCsv,
    };

    const requiredResult = validateRequired([row], {
      fields: Regulation_Schema.fields,
    });
    if (!requiredResult.valid) {
      dispatch(
        showSnackbar({
          message:
            requiredResult.message ||
            `${Regulation_Schema.fields?.[requiredResult.field]?.label || '필수값'} 을(를) 입력하세요.`,
          type: 'warning',
        })
      );
      return;
    }

    let result;
    try {
      result = await Save1Async(row);
    } catch {
      return;
    }

    const failed =
      result &&
      (result.IsSuccess === false || result.isSuccess === false);
    if (failed) {
      dispatch(
        showSnackbar({
          message:
            result?.Message ??
            result?.message ??
            '저장에 실패했습니다.',
          type: 'error',
        })
      );
      return;
    }

    dispatch(
      showSnackbar({
        message: '저장되었습니다.',
        type: 'success',
      })
    );
    onSaved?.();
  };

  return (
    <Regulation
      value={form}
      onChange={handleChange}
      onCancel={onCancel}
      onSave={SaveLogic1}
      deptItems={deptItems}
      userInfo={userInfo}
      searchInfo={searchInfo}
    />
  );
}

export default RegulationF;

